<template>
  <div class="page-wrapper">
    <Header />
    <!-- <FlashMessage v-if="this.messages.text != ''" :message="this.messages" /> -->

    <div class="py-3">
      <div class="container mx-auto max-w-[1200px] px-6 md:px-0">
        <div
          class="flex flex-col lg:flex-row justify-between items-center lg:items-stretch gap-8 rounded-3xl shadow-xl md:my-16 h-full overflow-hidden"
        >
          <div
            class="lg:px-20 flex flex-col py-5 lg:py-12 gap-6 justify-between h-full lg:text-left px-10 lg:w-1/2"
          >
            <h1
              class="text-2xl font-extrabold leading-tight tracking-tight text-primary sm:text-3xl lg:text-[40px] whitespace-nowrap"
            >
              Welcome to Your
              <span class="text-blues"
                >Learning <br />
                Journey!</span
              >
            </h1>

            <div class="content max-w-md text-slate-600 flex flex-col gap-4">
              <p>
                This learning space is designed to help you learn, reflect,
                apply and grow—one step at a time.
              </p>

              <div class="flex flex-col gap-2">
                <p class="font-semibold text-primary">Here, you can:</p>
                <ul class="flex flex-col gap-2.5">
                  <li
                    v-for="(point, index) in journeyPoints"
                    :key="index"
                    class="flex items-start gap-3"
                  >
                    <span
                      class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bg-[#E8F4FB] text-blues"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3 w-3"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M16.704 5.29a1 1 0 010 1.415l-7.5 7.5a1 1 0 01-1.415 0l-3.5-3.5a1 1 0 111.415-1.415L8.5 12.086l6.79-6.796a1 1 0 011.415 0z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </span>
                    <span class="text-sm">{{ point }}</span>
                  </li>
                </ul>
              </div>

              <p>
                Every learning touchpoint adds to your journey. Start with your
                first course, stay curious and keep moving forward.
              </p>
            </div>

            <div class="flex flex-row items-center gap-4 mt-2">
              <router-link
                :to="{ name: 'StudentMyCourse' }"
                class="bg-blues hover:bg-hover transition-colors shadow-lg shadow-blues/20 px-6 h-11 rounded-full text-white font-medium flex items-center justify-center gap-2"
              >
                Let's begin!
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </router-link>
            </div>
          </div>

          <div
            class="flex justify-center lg:justify-end gap-4 relative w-full lg:w-1/2 min-h-[220px] lg:min-h-0"
          >
            <img
              src="@/assets/new/index.jpeg"
              alt=""
              class="w-full h-full lg:rounded-r-3xl object-contain"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="container mx-auto max-w-[1200px] px-6 md:px-0">
      <!-- TOP LINKS -->
      <div
        v-if="submitted.length > 0 || submittedURL.length > 0"
        class="w-full"
      >
        <h2 class="mb-4 text-xl font-semibold text-[#0F172A]">Top Links</h2>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <!-- STUDY MATERIALS API -->
          <a
            v-for="(item, index) in submitted"
            :key="`material-${index}`"
            :href="item.pdf"
            download
            target="_blank"
            class="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-sky-200 hover:bg-sky-50 hover:shadow-md"
          >
            <div class="flex min-w-0 items-center gap-4">
              <!-- Document Icon -->
              <div
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600"
              >
                <svg
                  class="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 -960 960 960"
                  fill="currentColor"
                >
                  <path
                    d="M480-320 280-520l56-58 104 104v-326h80v326l104-104 56 58-200 200ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160H240Z"
                  />
                </svg>
              </div>

              <div class="min-w-0">
                <h3 class="truncate text-sm font-semibold text-slate-800">
                  {{ item.title }}
                </h3>

                <p class="mt-1 truncate text-xs text-slate-500">
                  {{ getShortName(item.pdf) }}
                </p>
              </div>
            </div>

            <!-- Arrow -->
            <span class="ml-4 text-slate-400 group-hover:text-sky-600">
              →
            </span>
          </a>

          <!-- URL LINKS API -->
          <a
            v-for="(item, index) in submittedURL"
            :key="`link-${index}`"
            :href="item.urllink"
            target="_blank"
            rel="noopener noreferrer"
            class="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-sky-200 hover:bg-sky-50 hover:shadow-md"
          >
            <div class="flex min-w-0 items-center gap-4">
              <!-- Link Icon -->
              <div
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600"
              >
                <svg
                  class="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M10 13a5 5 0 0 0 7.07.07l2-2a5 5 0 0 0-7.07-7.07l-1.14 1.14"
                  />
                  <path
                    d="M14 11a5 5 0 0 0-7.07-.07l-2 2A5 5 0 0 0 12 20l1.14-1.14"
                  />
                </svg>
              </div>

              <div class="min-w-0">
                <h3 class="truncate text-sm font-semibold text-slate-800">
                  {{ item.title }}
                </h3>

                <p class="mt-1 truncate text-xs text-slate-500">
                  {{ item.urllink }}
                </p>
              </div>
            </div>

            <!-- Arrow -->
            <span class="ml-4 text-slate-400 group-hover:text-sky-600">
              →
            </span>
          </a>
        </div>
      </div>
    </div>
    <div class="py-3 pb-20 mt-5">
      <div class="container mx-auto max-w-[1200px] px-6 md:px-0">
        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 shadow-xl rounded-3xl p-4"
          style="box-shadow: 0px 2px 20px 0px #105c8e26"
        >
          <!-- calender -->
          <div class="flex flex-col md:min-w-[330px]">
            <!-- <h1 class="text-[#0F172A] text-lg font-semibold px-6 pt-6">Calendar</h1> -->
            <div class="flex flex-col gap-3 md:min-w-[330px]">
              <Calendar @addFilterEmit="receiveCalEmit" />
              <!-- <vue-cal
                small
                ref="vuecal"
                events-on-month-view="short"
                :disable-views="['years', 'year', 'week', 'day']"
                :events="values"
                :on-event-click="onEventClick"
                class=""
              >
              </vue-cal> -->
            </div>
          </div>
          <!-- <pre>{{ submittedURL }}</pre> -->
          <!-- todo -->
          <div class="flex flex-col">
            <!-- <div v-else>No Data Found</div> -->

            <div class="flex flex-col gap-3 md:min-w-[330px]">
              <div
                class="bg-[#EDF8FF] flex justify-between py-2 px-3 rounded-xl items-center"
              >
                <h1 class="text-[#0085DB] text-lg font-semibold">
                  Tasks for Today
                </h1>
                <router-link :to="{ name: 'StudentTaskList' }">
                  <img
                    src="@/assets/chevron-down.png"
                    alt=""
                    srcset=""
                    class="object-contain w-5 h-5"
                  />
                </router-link>
              </div>
              <div class="flex flex-col gap-2 justify-start p-6 h-[330px]">
                <form @submit.prevent="addTask">
                  <div class="flex flex-col gap-2">
                    <p
                      v-if="success_message"
                      class="text-xs text-green-400 font-bold"
                    >
                      {{ success_message }}
                    </p>
                    <p
                      v-if="success_delemessage"
                      class="text-xs text-red-400 font-bold"
                    >
                      {{ success_delemessage }}
                    </p>

                    <div class="">
                      <input
                        v-model="newTask"
                        type="text"
                        class="border-b outline-none flex-1 p-1 w-full"
                        placeholder="+Add New Task"
                        @input="showButton = newTask.length > 0"
                        @keyup.enter="addTask"
                      />
                    </div>
                    <div
                      v-show="showButton"
                      class="flex justify-end items-center"
                    >
                      <div class="flex gap-1">
                        <button
                          class="text-white rounded transition-opacity cursor-pointer"
                        >
                          <img
                            src="@/assets/newd/Vector(1).svg"
                            alt=""
                            srcset=""
                            class="w-5 h-5 bg-[#EBEBEB] p-1"
                          />
                        </button>
                        <div @click="cleartask" class="cursor-pointer">
                          <img
                            src="@/assets/newd/closebutton.svg"
                            alt=""
                            class="w-6 h-6"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
                <ul class="overflow-y-auto" v-if="todolist.length > 0">
                  <li
                    v-for="task in todolist"
                    :key="task.id"
                    class="group py-2"
                  >
                    <div
                      class="flex justify-between bg-[#F4F4F4] w-full p-2 rounded-md"
                      v-if="task.completed == false"
                    >
                      <p class="text-sm break-words w-[230px] line-clamp-2">
                        {{ task.text }}
                      </p>
                      <div
                        class="flex gap-2 w-[25%] opacity-100 xl:opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      >
                        <!-- <button @click="completeTask(task.id)" class="">✅</button> -->
                        <button @click="completeTask(task.id)" class="">
                          <img
                            src="@/assets/newd/Vector(1).svg"
                            alt=""
                            srcset=""
                            class="w-6 h-6 bg-[#EBEBEB] p-1"
                          />
                        </button>

                        <button @click="openEditPopup(task)" class="">
                          <img
                            src="@/assets/newd/Group.svg"
                            alt=""
                            srcset=""
                            class="w-6 h-6 bg-[#EBEBEB] p-1"
                          />
                        </button>
                        <button @click="deleteTask(task.id)" class="">
                          <img
                            src="@/assets/newd/Vector.svg"
                            alt=""
                            srcset=""
                            class="w-6 h-6 bg-[#EBEBEB] p-1"
                          />
                        </button>
                      </div>
                    </div>
                  </li>
                </ul>

                <div v-else class="text-center">No task</div>
                <!-- Edit Popup -->
                <!-- <div
                v-if="isEditing"
                class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
              >
                <div class="bg-white p-4 rounded shadow-lg">
                  <h3 class="mb-2">Edit Task</h3>
                  <input
                    v-model="editTaskText"
                    type="text"
                    class="border p-1 w-full"
                  />
                  <div class="flex justify-end mt-2">
                    <button
                      @click="updateTask"
                      class="bg-blue-500 text-white px-2 py-1 rounded mr-2"
                    >
                      Update
                    </button>
                    <button
                      @click="isEditing = false"
                      class="bg-gray-500 text-white px-2 py-1 rounded"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div> -->
                <div
                  v-if="isEditing"
                  class="fixed bg-primary bg-opacity-30 top-0 left-0 right-0 bottom-0 z-50 flex items-center justify-center"
                >
                  <div
                    class="w-[90%] lg:w-[40%] bg-white rounded-xl p-6 lg:p-10 overflow-y-auto max-h-[600px]"
                  >
                    <div class="flex items-center justify-center">
                      <h3 class="text-xl font-bold text-textblues text-center">
                        Edit Task
                      </h3>
                    </div>

                    <div
                      class="w-full mt-4 flex flex-col p-6 relative border-gray-100 rounded-2xl"
                    >
                      <div class="w-full">
                        <form
                          ref="createTeamRef"
                          @submit.prevent="updateTask()"
                          class="flex flex-col gap-4 text-xs md:text-sm lg:text-base h-full"
                        >
                          <div>
                            <textarea
                              v-model="editTaskText"
                              name=""
                              id=""
                              cols="30"
                              rows="5"
                              class="border p-1 w-full"
                            ></textarea>
                          </div>

                          <div class="flex justify-center pb-4 mt-3 gap-10">
                            <button
                              @click="isEditing = false"
                              class="border text-gray-500 rounded-lg text-sm px-5 lg:px-10 font-semibold py-2"
                            >
                              Close
                            </button>
                            <button
                              class="border text-white rounded-lg bg-blues text-sm px-5 lg:px-10 font-semibold py-2"
                            >
                              Update
                            </button>
                          </div>

                          <div
                            v-if="editmessages"
                            class="text-green-400 text-lg font-bold"
                          >
                            {{ editmessages }}
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- <div class="" v-for="todo in [0, 1, 2, 3]" :key="todo">
                <div
                  class="flex justify-center gap-4 bg-[#105C8E1A] p-4 rounded-lg items-center"
                >
                  <img
                    src="@/assets/Vector(11).png"
                    alt=""
                    srcset=""
                    class="object contain w-4 h-4 mt-1"
                  />
                  <div class="flex flex-col gap-1">
                    <p class="text-sm font-bold text-[#333333] text-wrap">
                      15 Assignments Due
                    </p>
                  </div>
                </div>
              </div> -->
              </div>
            </div>
          </div>
          <!-- <pre>{{ taskList }}</pre> -->
          <!-- Announcements -->
          <div class="flex flex-col gap-3 md:min-w-[330px]">
            <div
              class="bg-[#EDF8FF] flex justify-between py-2 px-3 rounded-xl items-center"
            >
              <h1 class="text-[#0085DB] text-lg font-semibold">
                Announcements
              </h1>
              <router-link
                :to="{ name: 'StudentNotifications' }"
                v-if="hasAnnouncements"
              >
                <img
                  src="@/assets/chevron-down.png"
                  alt=""
                  srcset=""
                  class="object-contain w-5 h-5"
                />
              </router-link>
            </div>

            <div class="p-6 w-full h-[380px] overflow-y-auto">
              <!-- Check if there's any data to display -->
              <div v-if="hasAnnouncements" class="flex flex-col gap-5">
                <!-- Notifications Section -->
                <div v-if="notificationList.length > 0">
                  <h2 class="text-sm font-bold text-[#0F172A] mb-3">
                    Notifications
                  </h2>
                  <div class="flex flex-col gap-3">
                    <div
                      class="border-b pb-3 border-gray-300 last:border-b-0"
                      v-for="notifi in notificationList.slice(0, 3)"
                      :key="'notif-' + notifi.id"
                    >
                      <div class="flex gap-3">
                        <img
                          src="@/assets/Group 610.png"
                          alt=""
                          class="object-contain w-6 h-6 mt-1 flex-shrink-0"
                        />
                        <div class="flex flex-col gap-1 flex-1 min-w-0">
                          <h3
                            class="text-sm font-semibold text-black break-words line-clamp-2"
                          >
                            {{ notifi.topic }}
                          </h3>
                          <p
                            class="text-xs text-[#ADADAD] break-words line-clamp-2"
                          >
                            {{ notifi.message }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Accepted Assignments Section -->
                <div
                  v-if="
                    taskList.accepted_assignments &&
                    taskList.accepted_assignments.length > 0
                  "
                >
                  <h2 class="text-sm font-bold text-green-600 mb-3">
                    Accepted Assignments
                  </h2>
                  <div class="flex flex-col gap-3">
                    <div
                      class="border-b pb-3 border-gray-300 last:border-b-0"
                      v-for="assignment in taskList.accepted_assignments"
                      :key="'accepted-' + assignment.id"
                    >
                      <div class="flex gap-3">
                        <img
                          src="@/assets/Group 610.png"
                          alt=""
                          class="object-contain w-6 h-6 mt-1 flex-shrink-0"
                        />
                        <div class="flex flex-col gap-1 flex-1 min-w-0">
                          <router-link
                            :to="`/student/dashboard/${assignment.course_id}/assignments`"
                            class="text-sm font-semibold text-black break-words line-clamp-2 hover:underline cursor-pointer hover:text-blue-600"
                          >
                            {{ assignment.title }}
                          </router-link>
                          <p class="text-xs text-green-600">
                            <span class="font-medium">Status:</span>
                            {{ assignment.verify_status }}
                          </p>
                          <p class="text-xs text-gray-600">
                            <span class="font-medium">Course Name:</span>
                            {{ assignment.course_title }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Rejected Assignments Section -->
                <div
                  v-if="
                    taskList.rejected_assignments &&
                    taskList.rejected_assignments.length > 0
                  "
                >
                  <h2 class="text-sm font-bold text-red-600 mb-3">
                    Rejected Assignments
                  </h2>
                  <div class="flex flex-col gap-3">
                    <div
                      class="border-b pb-3 border-gray-300 last:border-b-0"
                      v-for="assignment in taskList.rejected_assignments"
                      :key="'rejected-' + assignment.id"
                    >
                      <div class="flex gap-3">
                        <img
                          src="@/assets/Group 610.png"
                          alt=""
                          class="object-contain w-6 h-6 mt-1 flex-shrink-0"
                        />
                        <div class="flex flex-col gap-1 flex-1 min-w-0">
                          <router-link
                            :to="`/student/dashboard/${assignment.course_id}/assignments`"
                            class="text-sm font-semibold text-black break-words line-clamp-2 hover:underline cursor-pointer hover:text-blue-600"
                          >
                            {{ assignment.title }}
                          </router-link>
                          <p class="text-xs text-red-600">
                            <span class="font-medium">Status:</span>
                            {{ assignment.verify_status }}
                          </p>
                          <p class="text-xs text-gray-600">
                            <span class="font-medium">Course Name:</span>
                            {{ assignment.course_title }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Calendar Events Section -->
                <div
                  v-if="
                    taskList.calendar_items &&
                    taskList.calendar_items.length > 0
                  "
                >
                  <h2 class="text-sm font-bold text-blue-600 mb-3">Events</h2>
                  <div class="flex flex-col gap-3">
                    <div
                      class="border-b pb-3 border-gray-300 last:border-b-0"
                      v-for="event in taskList.calendar_items"
                      :key="'event-' + event.id"
                    >
                      <div class="flex gap-3">
                        <img
                          src="@/assets/Group 610.png"
                          alt=""
                          class="object-contain w-6 h-6 mt-1 flex-shrink-0"
                        />
                        <div
                          class="flex justify-between items-center gap-1 flex-1 min-w-0"
                        >
                          <div class="flex flex-col gap-2">
                            <a
                              v-if="event.url"
                              :href="event.url"
                              target="blank"
                              class="text-sm hover:text-blue-600 font-semibold text-black break-words line-clamp-2 hover:underline cursor-pointer hover:text-blue-60"
                              >{{ event.title }}</a
                            >
                            <!-- <h3
                            class="text-sm font-semibold text-black break-words line-clamp-2 hover:underline cursor-pointer hover:text-blue-60"
                          >
                            {{ event.title }}
                          </h3> -->
                            <!-- Blue Right Arrow Icon SVG -->

                            <p
                              class="text-xs text-blue-600"
                              v-if="event.start_date"
                            >
                              Event Date: {{ formatDate(event.start_date) }}
                            </p>
                            <p class="text-xs text-gray-600">
                              <span class="font-medium">Course Name:</span>
                              {{ event.course_title }}
                            </p>
                          </div>
                          <router-link
                            :to="`/student/dashboard/${event.course_data}/classes`"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              class="h-4 w-4 text-blue-400 hover:text-blue-600"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                                clip-rule="evenodd"
                              />
                            </svg>
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Overdue Assignments Section -->
                <div
                  v-if="
                    taskList.overdue_assignments &&
                    taskList.overdue_assignments.length > 0
                  "
                >
                  <h2 class="text-sm font-bold text-orange-600 mb-3">
                    Overdue Assignments
                  </h2>
                  <div class="flex flex-col gap-3">
                    <div
                      class="border-b pb-3 border-gray-300 last:border-b-0"
                      v-for="assignment in taskList.overdue_assignments"
                      :key="'overdue-' + assignment.id"
                    >
                      <div class="flex gap-3">
                        <img
                          src="@/assets/Group 610.png"
                          alt=""
                          class="object-contain w-6 h-6 mt-1 flex-shrink-0"
                        />
                        <div class="flex flex-col gap-1 flex-1 min-w-0">
                          <router-link
                            :to="`/student/dashboard/${assignment.course_id}/assignments`"
                            class="text-sm font-semibold text-black break-words line-clamp-2 hover:underline cursor-pointer hover:text-blue-600"
                          >
                            {{ assignment.title }}
                          </router-link>
                          <p class="text-xs text-orange-600">
                            <span class="font-medium">Status:</span>
                            {{ assignment.verify_status }}
                          </p>
                          <p class="text-xs text-gray-600">
                            <span class="font-medium">Course Name:</span>
                            {{ assignment.course_title }}
                          </p>
                          <p
                            class="text-xs text-red-500"
                            v-if="assignment.due_date"
                          >
                            <span class="font-medium">Due Date :</span>
                            {{ assignment.due_date }}
                          </p>
                          <p
                            class="text-xs text-orange-500"
                            v-if="assignment.submit_date"
                          >
                            <span class="font-medium"> Submit Date :</span>
                            {{ assignment.submit_date }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Submitted Assignments Section -->
                <div
                  v-if="
                    taskList.submitted_assignments &&
                    taskList.submitted_assignments.length > 0
                  "
                >
                  <h2 class="text-sm font-bold text-purple-600 mb-3">
                    Submitted Assignments
                  </h2>
                  <div class="flex flex-col gap-3">
                    <div
                      class="border-b pb-3 border-gray-300 last:border-b-0"
                      v-for="assignment in taskList.submitted_assignments"
                      :key="'submitted-' + assignment.id"
                    >
                      <div class="flex gap-3">
                        <img
                          src="@/assets/Group 610.png"
                          alt=""
                          class="object-contain w-6 h-6 mt-1 flex-shrink-0"
                        />
                        <div class="flex flex-col gap-1 flex-1 min-w-0">
                          <router-link
                            :to="`/student/dashboard/${assignment.course_id}/assignments`"
                            class="text-sm font-semibold text-black break-words line-clamp-2 hover:underline cursor-pointer hover:text-blue-600"
                          >
                            {{ assignment.title }}
                          </router-link>
                          <p
                            class="text-xs text-purple-600"
                            v-if="assignment.submit_date"
                          >
                            <span class="font-medium">Status:</span>
                            {{ "submitted" }}
                          </p>
                          <p class="text-xs text-gray-600">
                            <span class="font-medium">Course Name:</span>
                            {{ assignment.course_title }}
                          </p>
                          <p
                            class="text-xs text-purple-600"
                            v-if="assignment.submit_date"
                          >
                            <span class="font-medium"> Submit Date :</span>
                            {{ assignment.submit_date }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Empty state -->
              <div
                v-else
                class="flex items-center justify-center h-full text-gray-500"
              >
                No Announcements
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- My Learning -->
    <div class="pt-10 pb-20">
      <div class="container mx-auto max-w-[1200px] flex flex-col gap-10">
        <div>
          <div class="flex flex-row items-center justify-between px-5 lg:px-0">
            <div class="flex flex-col md:flex-row items-baseline gap-3">
              <p class="text-[#0F172A] text-xl lg:text-3xl font-semibold">
                My Active Courses
              </p>
              <!-- <span
                class="bg-blues hover:bg-hover text-white px-3 py-1 rounded-full text-sm font-medium"
              >
                {{ this.userInfo?.current_semester?.department }} - Semester
                {{ this.userInfo?.current_semester?.semester }}
              </span> -->
            </div>
            <router-link
              :to="{ name: 'StudentMyCourse' }"
              class="text-md text-[#0F172A] font-medium flex gap-1 items-center hover:text-indigo-600 transition-colors"
            >
              View All
              <img
                src="@/assets/newd/chevron-down(1).png"
                alt=""
                class="w-4 h-4"
              />
            </router-link>
          </div>

          <div v-if="loading" class="flex items-center justify-center">
            <img src="@/assets/newd/loading.gif" alt="" />
          </div>
          <div v-else class="mt-6 px-5 lg:px-0">
            <div v-if="this.courselist && this.courselist.course?.length">
              <div
                v-if="this.courselist"
                class="grid grid-cols-1 lg:grid-cols-3 gap-4"
              >
                <div
                  v-for="course in this.courselist.course.slice(0, 3)"
                  :key="course"
                  class="bg-white shadow-[0px_2px_30px_0px_rgba(1,0,0,0.1)] rounded-lg p-4 sm:p-6 h-auto"
                >
                  <div
                    @click="getDashboardOrPreviewUp(course.id)"
                    class="cursor-pointer"
                  >
                    <div class="relative flex justify-center items-center">
                      <img
                        v-if="course.thumbnail"
                        class="object-cover h-[180px] sm:h-[200px] w-full rounded-lg"
                        :src="course.thumbnail"
                        alt="Course Thumbnail"
                      />
                      <img
                        v-else
                        class="object-cover h-[180px] sm:h-[200px] w-full rounded-lg"
                        src="@/assets/coding.svg"
                        alt="Placeholder"
                      />
                    </div>
                    <div class="text-center flex flex-col gap-2 mt-2">
                      <p
                        class="text-sm sm:text-base text-gray-600 font-semibold text-left truncate"
                      >
                        {{ course.title }}
                      </p>
                      <p class="text-xs sm:text-sm text-gray-600 text-left">
                        {{ course.section_count }} Module
                      </p>

                      <p
                        @click="getDashboardOrPreviewUp(course.id)"
                        class="mt-3 cursor-pointer py-2 text-xs sm:text-sm rounded-lg border border-[#0085DB] hover:bg-hover hover:text-white text-[#0F172A] font-medium text-center"
                      >
                        Continue Learning
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-lg font-normal text-center">
              No data Found
            </div>
          </div>
        </div>

        <!-- <div>
          <div class="flex flex-row items-center justify-between">
            <p class="text-[#0F172A] text-3xl font-semibold">
              Additional Courses
            </p>
            <router-link
              :to="{ name: 'StudentCommunity' }"
              class="text-md text-[#0F172A] font-medium flex gap-1 items-center"
              >View All
              <img
                src="@/assets/newd/chevron-down(1).png"
                alt=""
                srcset=""
                class="w-4 h-4"
            /></router-link>
          </div>
          <div v-if="loading" class="flex items-center justify-center">
            <img src="@/assets/newd/loading.gif" alt="" />
          </div>
          <div v-else class="mt-6">
            <div v-if="this.courseDetails.length > 0">
              <div
                v-if="this.courseDetails[0]"
                class="grid grid-cols-1 gap-4 mt-6"
              >
                <div
                  v-for="course in this.courseDetails[0].course.slice(0, 3)"
                  :key="course"
                  class="bg-white shadow-[0px_2px_30px_0px_rgba(1,0,0,0.1)] rounded-lg p-4 sm:p-6 h-auto"
                >
                  <div
                    @click="getDashboardOrPreviewUp(course.id)"
                    class="cursor-pointer lg:flex lg:gap-5 w-full"
                  >
                    <div
                      class="relative flex justify-center items-center lg:w-[30%]"
                    >
                      <img
                        v-if="course.thumbnail"
                        class="object-cover h-[180px] sm:h-[200px] w-full rounded-lg"
                        :src="course.thumbnail"
                        alt="Course Thumbnail"
                      />
                      <img
                        v-else
                        class="object-cover h-[180px] sm:h-[200px] w-full rounded-lg"
                        src="@/assets/coding.svg"
                        alt="Placeholder"
                      />
                    </div>
                    <div
                      class="text-center flex flex-col lg:justify-between gap-2 mt-2 lg:w-[70%]"
                    >
                      <p
                        class="text-sm sm:text-base lg:text-lg text-gray-600 font-semibold text-left truncate"
                      >
                        {{ course.title }}
                      </p>
                      <p class="text-xs sm:text-sm text-gray-600 text-left">
                        {{ course.sections.length }} Units
                      </p>
                      <p
                        class="text-xs sm:text-sm text-gray-600 text-left line-clamp-2"
                      >
                        {{ course.short_description }}
                      </p>
                      <div class="lg:w-[400px] flex flex-col gap-2">
                        <div class="flex items-center justify-between w-full">
                          <div>
                            <p
                              class="text-sm"
                              v-if="course.completion_percentage < 100"
                            >
                              In progress
                            </p>
                            <p v-else class="text-sm">Complete</p>
                          </div>
                          <div>
                            <p class="text-sm">
                              {{ course.completion_percentage }}%
                            </p>
                          </div>
                        </div>

                        <div
                          class="relative w-full bg-gray-200 rounded-full h-4"
                        >
                          <div
                            class="absolute left-0 top-0 h-4 bg-blues rounded-full transition-all duration-300"
                            :style="{
                              width: course.completion_percentage + '%',
                            }"
                          ></div>
                        </div>
                      </div>
                      <p
                        @click="getDashboardOrPreviewUp(course.id)"
                        class="mt-3 cursor-pointer py-2 text-xs sm:text-sm rounded-lg border border-[#0085DB] lg:w-[200px] text-[#0F172A] font-medium text-center"
                      >
                        Continue Learning
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-lg font-normal text-center">
              No data Found
            </div>

           
          </div>
        </div> -->
      </div>
    </div>

    <Footer />
    <SurveyPopup
      v-model="showSurveyPopup"
      :survey-id="currentSurveyId"
      @survey-submitted="onSurveySubmitted"
    />
  </div>
</template>

<script>
import Header from "@/components/Student/Header.vue";
import Footer from "@/components/Footer.vue";
import axios from "axios";
// import FlashMessage from "@/components/flashMessage.vue";
import { mapGetters } from "vuex";
import VueCal from "vue-cal";
import "vue-cal/dist/vuecal.css";

import Calendar from "@/components/Calendar.vue";
import SurveyPopup from "./SurveyPopup.vue";

export default {
  name: "Home",
  data() {
    return {
      logotitle: process.env.VUE_APP_TITLE,
      journeyPoints: [
        "Access your courses and learning resources",
        "Complete activities and assessments",
        "Track your progress and completed courses",
        "Revisit important concepts whenever required",
        "Receive updates on new content, upcoming sessions and next steps",
      ],
      courselist: [],
      blog_list: "",
      webinar_List: "",
      title: "",
      purchased_course: "",
      allyourcourse: "",
      listOfcourse: [],
      courseDetails: null,
      courseDetails: [],
      progress: 70,
      loading: true,
      notificationList: [],
      todolist: [],
      newTask: "",
      isEditing: false,
      editTaskText: "",
      editTaskId: null,
      showButton: false,
      editmessages: "",
      success_message: "",
      success_delemessage: "",
      currentYear: "",
      currentMonth: "",
      currentDate: "",
      fullDate: "",
      taskList: "",
      taskList: {
        accepted_assignments: [],
        rejected_assignments: [],
        calendar_items: [],
        overdue_assignments: [],
      },
      submitted: [],
      submittedURL: [],
      currentSurveyId: null,
      showSurveyPopup: false,
    };
  },

  computed: {
    ...mapGetters(["userInfo"]),

    hasAnnouncements() {
      const hasNotifications =
        this.notificationList && this.notificationList.length > 0;
      const hasAccepted = this.taskList?.accepted_assignments?.length > 0;
      const hasRejected = this.taskList?.rejected_assignments?.length > 0;
      const hasEvents = this.taskList?.calendar_items?.length > 0;
      const hasOverdue = this.taskList?.overdue_assignments?.length > 0;

      return (
        hasNotifications ||
        hasAccepted ||
        hasRejected ||
        hasEvents ||
        hasOverdue
      );
    },
  },

  components: {
    Header,
    Footer,
    // FlashMessage,
    VueCal,
    Calendar,
    SurveyPopup,
  },
  mounted() {
    this.getSubmittedURL();
    // setTimeout(() => {
    //   this.loading = false;
    // }, 2000);
    this.getNotifications();
    this.getTodo();
    this.getSubmittedData();
    this.title = process.env.VUE_APP_Title;
    if (!localStorage.getItem("previewIgnoreList")) {
      localStorage.setItem("previewIgnoreList", JSON.stringify([]));
    }
  },
  async created() {
    this.getSurveyList();
    const today = new Date();
    this.currentDate = today.getDate();
    this.currentMonth = today.getMonth() + 1;
    this.currentYear = today.getFullYear();
    this.fullDate = `${this.currentYear}-${this.currentMonth}-${this.currentDate}`;

    await axios
      .get(
        `${process.env.VUE_APP_API}/student-buy-course-percentage/?Key=${this.userInfo.token_key}`,
      )
      .then((response) => {
        if (response.data.status == 200) {
          this.courseDetails = response.data.data;
        }
      })
      .catch((err) => {
        console.log("error", err);
      })
      .finally(() => {
        setTimeout(() => {
          this.loading = false;
        }, 600);
      });
    await axios
      .get(
        `${process.env.VUE_APP_API}/student-semester-get/${this.userInfo.user.id}/?Key=${this.userInfo.token_key}&semester__id=${this.userInfo.current_semester.id}`,
      )
      .then((response) => {
        if (response.data.data.status == 200) {
          const semesterData = response.data.data.data?.[0]?.semester?.[0];

          if (semesterData) {
            this.courselist = {
              ...semesterData,
              course: Array.isArray(semesterData.course)
                ? semesterData.course.filter(
                    (course) => course.published_course?.published === true,
                  )
                : [],
            };
          } else {
            this.courselist = null;
          }
        }
      });
  },
  methods: {
    onSurveySubmitted() {
      this.showSurveyPopup = false;
      this.currentSurveyId = null;

      this.getSurveyList();
    },
    getSurveyList() {
      axios
        .get(
          `${process.env.VUE_APP_API}/survey/list/?Key=${this.userInfo.token_key}&is_published=true`,
        )
        .then((res) => {
          if (res.data.data.status !== 200) {
            this.currentSurveyId = null;
            this.showSurveyPopup = false;
            return;
          }

          const surveys = res.data.data.data || [];

          if (!surveys.length) {
            this.currentSurveyId = null;
            this.showSurveyPopup = false;
            return;
          }

          // First survey
          const survey = surveys[0];

          if (!survey) {
            this.currentSurveyId = null;
            this.showSurveyPopup = false;
            return;
          }

          // Already submitted -> don't show popup
          if (survey.is_submitted) {
            this.currentSurveyId = null;
            this.showSurveyPopup = false;
            return;
          }

          // Not published
          if (!survey.is_published) {
            this.currentSurveyId = null;
            this.showSurveyPopup = false;
            return;
          }

          // Start date
          if (survey.start_date) {
            const startDate = new Date(survey.start_date);
            if (new Date() < startDate) {
              this.currentSurveyId = null;
              this.showSurveyPopup = false;
              return;
            }
          }

          // End date
          if (survey.end_date) {
            const endDate = new Date(survey.end_date);
            if (new Date() > endDate) {
              this.currentSurveyId = null;
              this.showSurveyPopup = false;
              return;
            }
          }

          this.currentSurveyId = survey.id;
          this.showSurveyPopup = true;
        })
        .catch((error) => {
          console.error("Survey Error:", error);
          this.currentSurveyId = null;
          this.showSurveyPopup = false;
        });
    },
    getSubmittedURL() {
      axios
        .get(
          `${process.env.VUE_APP_API}/course-external-links/list/?Key=${this.userInfo.token_key}`,
        )
        .then((res) => {
          this.submittedURL = res.data.data || [];
        });
    },
    getShortName(url) {
      if (!url) return "";
      const fileName = url.split("/").pop();
      return fileName.length > 20
        ? fileName.substring(0, 20) + "..."
        : fileName;
    },

    getSubmittedData() {
      axios
        .get(
          `${process.env.VUE_APP_API}/pdf-upload-list/?Key=${this.userInfo.token_key}`,
        )
        .then((res) => {
          this.submitted = res.data.data.data;
        });
    },
    formatDate(dateString) {
      if (!dateString) return "";

      const date = new Date(dateString);
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };

      // Check if date string contains time information
      if (dateString.includes("T")) {
        return date.toLocaleDateString("en-US", options);
      } else {
        // Just date, no time
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
    },
    receiveCalEmit(data) {
      axios
        .get(
          `${process.env.VUE_APP_API}/today-status/?user_id=${this.userInfo.user.id}&Key=${this.userInfo.token_key}&date=${data}`,
        )
        .then((res) => {
          this.taskList = res.data;
        });

      axios
        .get(
          `${process.env.VUE_APP_API}/to-do-get/${this.userInfo.user.id}/?Key=${this.userInfo.token_key}&date=${data}`,
        )
        .then((response) => {
          if (response.data.data.status == 200) {
            this.todolist = response.data.data.data;
          }
        });

      axios
        .get(
          `${process.env.VUE_APP_API}/notifications/${this.userInfo.user.id}/?Key=${this.userInfo.token_key}&date=${data}`,
        )
        .then((response) => {
          if (response) {
            this.notificationList = response.data.data.data;
          }
        });
    },

    cleartask() {
      this.newTask = "";
      this.showButton = false;
    },
    getDashboardOrPreviewUp(id) {
      if (localStorage.getItem("previewIgnoreList")) {
        let preList = new Set(
          JSON.parse(localStorage.getItem("previewIgnoreList")),
        );

        if (preList.size != 0) {
          if (preList.has(id.toString())) {
            this.$router.push({
              name: "StudentCourseProgress",
              params: { course_id: id },
            });
          } else {
            this.$router.push({
              name: "StudentCourse",
              params: { courseid: id },
            });
          }
        } else {
          this.$router.push({
            name: "StudentCourse",
            params: { courseid: id },
          });
        }
      } else {
        this.$router.push({ name: "StudentCourse", params: { courseid: id } });
      }
    },
    async getNotifications() {
      await axios
        .get(
          `${process.env.VUE_APP_API}/notifications/${this.userInfo.user.id}/?Key=${this.userInfo.token_key}&year=${this.currentYear}&month=${this.currentMonth}&date=${this.fullDate}`,
        )
        .then((response) => {
          if (response) {
            this.notificationList = response.data.data.data;
          }
        });
    },
    openEditPopup(task) {
      this.editTaskText = task.text;
      this.editTaskId = task.id;
      this.isEditing = true;
    },
    addTask() {
      if (this.newTask.trim()) {
        let bodyVal = {
          text: this.newTask,
          user: this.userInfo.user.id,
        };

        axios
          .post(
            `${process.env.VUE_APP_API}/to-do-create/?Key=${this.userInfo.token_key}`,
            bodyVal,
          )
          .then((response) => {
            if (response.data.status == 200) {
              this.success_message = response.data.message;
              setTimeout(() => {
                this.success_message = "";
              }, 900);
              this.newTask = "";
              this.showButton = false;
              this.getTodo();
            }
          })
          .finally(() => {
            this.loaders = false;
          });
      }
    },
    async getTodo() {
      await axios
        .get(
          `${process.env.VUE_APP_API}/to-do-get/${this.userInfo.user.id}/?Key=${this.userInfo.token_key}&search=false`,
        )
        .then((response) => {
          if (response.data.data.status == 200) {
            this.todolist = response.data.data.data;
          }
        });
    },
    updateTask() {
      let formdata = {
        text: this.editTaskText,
      };

      axios
        .put(
          `${process.env.VUE_APP_API}/to-do-text-edit/${this.editTaskId}/?Key=${this.userInfo.token_key}`,
          formdata,
        )
        .then((response) => {
          if (response.data.status == 200) {
            this.editmessages = response.data.message;

            setTimeout(() => {
              this.editmessages = "";
              this.isEditing = false;
              this.getTodo();
            }, 2000);
          }
        })
        .finally(() => {
          this.loaders = false;
        });
    },
    deleteTask(id) {
      axios
        .delete(
          `${process.env.VUE_APP_API}/to-do-update/${id}/?Key=${this.userInfo.token_key}`,
        )
        .then((response) => {
          if (response.data.status == 200) {
            this.success_delemessage = response.data.message;
            setTimeout(() => {
              this.success_delemessage = "";
            }, 900);
            this.getTodo();
          }
        });
    },
    completeTask(id) {
      let completedata = {
        completed: true,
      };
      axios
        .put(
          `${process.env.VUE_APP_API}/to-do-update/${id}/?Key=${this.userInfo.token_key}`,
          completedata,
        )
        .then((response) => {
          if (response.data.status == 200) {
            this.success_message = response.data.message;
            setTimeout(() => {
              this.success_message = "";
            }, 900);
            this.getTodo();
          }
        });
    },
  },
};
</script>

<style scoped>
.page-wrapper {
  position: relative;
}
</style>
